import { decodeFunctionResult, encodeFunctionData, keccak256, type Abi, type Address, type Hex } from "viem";
import { ELYSIUM, sameIdentity } from "../config/elysium";
import { ponsRewardNftAbi } from "../contracts/pons-reward-nft";
import type { Eip1193Provider } from "./eip1193";
import { readRpc, type RpcReadOptions } from "./read-rpc";
import { QUOTE_MULTICALL, readQuoteContracts, type ContractRead } from "./quote-contract-reads";

export type ElysiumObservation = { blockNumber: bigint; blockHash: Hex; observedAt: number };
/** Verify chain, fresh coherent head, code and bindings under the current operation.
 * Mint supplies its response-local server clock; existing read/swap callers keep their clock. */
export async function observeElysium(provider: Eip1193Provider,
  assertCurrent: () => void, options: RpcReadOptions = {}, now: () => number = Date.now): Promise<ElysiumObservation> {
  const head = await readElysiumHead(provider, assertCurrent, options, now);
  await checkElysiumContracts(provider, head, assertCurrent, options);
  await confirmElysiumHead(provider, head, assertCurrent, options);
  return { ...head, observedAt: performance.now() };
}

export async function readElysiumHead(provider: Eip1193Provider,
  assertCurrent: () => void, options: RpcReadOptions = {}, now: () => number = Date.now): Promise<ElysiumObservation & { timestamp: Hex }> {
  const request = async (method: Parameters<typeof readRpc>[1]["method"], params: readonly unknown[] = []) => {
    assertCurrent(); const result = await readRpc(provider, { method, params }, options); assertCurrent(); return result;
  };
  const [chain, head] = await Promise.all([
    request("eth_chainId"),
    request("eth_getBlockByNumber", ["latest", false]),
  ]);
  if (chain !== `0x${ELYSIUM.chainId.toString(16)}`) throw new Error("ELYSIUM_CHAIN_MISMATCH");
  const header = head as { number?: string; hash?: Hex; timestamp?: string } | null;
  if (!header?.number || !/^0x[0-9a-f]+$/i.test(header.number) || !header.hash || !/^0x[0-9a-f]{64}$/i.test(header.hash) ||
    !header.timestamp || !/^0x[0-9a-f]+$/i.test(header.timestamp)) throw new Error("ELYSIUM_HEAD_INVALID");
  if (BigInt(header.number) < ELYSIUM.deploymentBlock) throw new Error("ELYSIUM_HEAD_BEFORE_DEPLOYMENT");
  const age = BigInt(Math.floor(now() / 1000)) - BigInt(header.timestamp);
  if (age < -30n || age > 120n) throw new Error("ELYSIUM_HEAD_STALE");
  return { blockNumber: BigInt(header.number), blockHash: header.hash, timestamp: header.timestamp as Hex, observedAt: performance.now() };
}

export async function checkElysiumContracts(provider: Eip1193Provider, head: ElysiumObservation,
  assertCurrent: () => void, options: RpcReadOptions = {}, multicall = false,
  additionalCalls: readonly ContractRead[] = []): Promise<unknown[]> {
  const block = `0x${head.blockNumber.toString(16)}` as Hex;
  const request = async (method: Parameters<typeof readRpc>[1]["method"], params: readonly unknown[] = []) => {
    assertCurrent(); const result = await readRpc(provider, { method, params }, options); assertCurrent(); return result;
  };
  const read = async (to: Address, abi: Abi, functionName: string, args?: readonly unknown[]) => {
    const data = encodeFunctionData({ abi, functionName, args });
    const result = await request("eth_call", [{ to, data }, block]);
    if (typeof result !== "string") throw new Error("ELYSIUM_BINDING_INVALID");
    return decodeFunctionResult({ abi, functionName, data: result as Hex });
  };
  // NFT readiness depends on its source, vault and collection bindings. V4 has
  // no V3 pool/factory contract; swap readiness belongs to the trade reader.
  const addresses = [ELYSIUM.token, ELYSIUM.vault, ELYSIUM.collection, ELYSIUM.readiness,
    ...(multicall ? [QUOTE_MULTICALL.address] : [])];
  const code = await Promise.all(addresses
    .map(address => request("eth_getCode", [address, block])));
  if (code.some(value => typeof value !== "string" || !/^0x(?:[0-9a-f]{2})+$/i.test(value))) throw new Error("ELYSIUM_CODE_MISSING");
  if (multicall && keccak256(code[4] as Hex) !== QUOTE_MULTICALL.codeHash) throw new Error("QUOTE_MULTICALL_CODE_MISMATCH");
  const calls: ContractRead[] = [
    ...["FEE_VAULT", "RANDOMNESS_ADAPTER", "INSTANCE_ID", "AUTHORIZATION_SIGNER"].map(functionName =>
      ({ to: ELYSIUM.collection, abi: ponsRewardNftAbi, functionName })),
    ...additionalCalls,
  ];
  const bindings = multicall ? await readQuoteContracts(provider, calls, block, assertCurrent, options) :
    await Promise.all(calls.map(call => read(call.to, call.abi, call.functionName, call.args)));
  if (bindings.slice(0, 4).some((value, index) => typeof value !== "string" || !sameIdentity(value,
    [ELYSIUM.vault, ELYSIUM.readiness, ELYSIUM.instanceId, ELYSIUM.signer][index]!)))
    throw new Error("ELYSIUM_BINDING_MISMATCH");
  return bindings.slice(4);
}

export async function confirmElysiumHead(provider: Eip1193Provider, head: ElysiumObservation,
  assertCurrent: () => void, options: RpcReadOptions = {}): Promise<void> {
  assertCurrent();
  const confirmed = await readRpc(provider, { method: "eth_getBlockByNumber", params: [`0x${head.blockNumber.toString(16)}`, false] }, options);
  assertCurrent();
  if (!confirmed || typeof confirmed !== "object" || !sameIdentity((confirmed as { hash?: string }).hash, head.blockHash))
    throw new Error("ELYSIUM_HEAD_REORG");
}
